import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DialogFooter } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { sendPhoneCode, verifyPhoneCode } from '../../lib/api';

export const PhoneCodeVerification = ({ initialPhone, onVerified }) => {
  const [phone, setPhone] = useState(initialPhone || '');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('phone');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const requestCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await sendPhoneCode(phone);
      setInfo(data.message);
      setStep('code');
    } catch (e) {
      setError(e.response?.data?.detail || "We couldn't send the code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await verifyPhoneCode(phone, code);
      toast.success('Phone number verified');
      onVerified(data.phone_number);
    } catch (e) {
      setError(e.response?.data?.detail || "We couldn't verify the code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="space-y-4 py-4">
        {step === 'phone' ? (
          <div className="space-y-2">
            <Label htmlFor="verify-phone">Phone Number (with country code)</Label>
            <Input id="verify-phone" type="tel" placeholder="+1 555 123 4567" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 rounded-xl font-mono" data-testid="verify-phone-input" />
            <p className="text-xs text-muted-foreground">We'll send a 6-digit code to this number on WhatsApp.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="verify-code">6-digit code</Label>
            <Input id="verify-code" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className="h-12 rounded-xl font-mono tracking-[0.4em] text-center text-lg" data-testid="verify-code-input" />
            {info && <p className="text-xs text-muted-foreground" data-testid="verify-code-info">{info}</p>}
            <button type="button" className="text-xs underline text-muted-foreground" onClick={() => { setStep('phone'); setCode(''); setError(null); }} data-testid="verify-change-number-btn">Change number or resend code</button>
          </div>
        )}
        {error && <p className="text-sm text-red-600" data-testid="verify-phone-error">{error}</p>}
      </div>
      <DialogFooter>
        {step === 'phone' ? (
          <Button onClick={requestCode} disabled={busy || phone.replace(/\D/g, '').length < 10} className="w-full rounded-xl" data-testid="send-phone-code-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Send code
          </Button>
        ) : (
          <Button onClick={confirm} disabled={busy || code.length !== 6} className="w-full rounded-xl" data-testid="confirm-phone-code-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Verify
          </Button>
        )}
      </DialogFooter>
    </>
  );
};
